// ─────────────────────────────────────────
//  AGENTIC AI LOOP
//  ─────────────────────────────────────────
//  Orchestrator flow:
//  1. Classify request (simple vs complex)
//  2. If complex → Planner breaks into steps
//  3. Coder generates code (per step or all at once)
//  4. Run in iframe, capture errors
//  5. Debugger fixes errors (max 3 retries)
//  6. Reviewer checks quality before done
// ─────────────────────────────────────────

import { S, sb } from './state.js';
import { sleep, extractCode, parsePlanSteps, showToast } from './utils.js';
import { aiRequest, aiRequestStream, aiRequestSilent, MODELS } from './ai.js';
import { applyCode, notifyCodeUpdate } from './editor.js';
import { runCode, runCodeSilent } from './preview.js';
import { addMsg, removeTyping, showTyping, showSteps, updateStep, hideSteps, updateStatusPill, setStatus } from './ui.js';
import { buildSystem } from './prompts.js';
import { autoSaveProject } from './projects.js';
import { saveConversation } from './conversations.js';
import { fullStackBuild, isProPlus, checkCloudSyncNeeded, isFullStackMode } from './fullstack.js';
import { cacheTemplate } from './templates.js';

const AGENT_MAX_RETRIES = 3;
let _iframeErrors = [];
let _visualReport = null;

// Listen for errors and visual reports from the preview iframe
window.addEventListener('message', (e) => {
  if (e.data?.type === 'vc-error') {
    _iframeErrors.push(e.data.message);
  }
  if (e.data?.type === 'vc-visual-report') {
    _visualReport = e.data.report;
  }
});

// ── Detect if a prompt requires full-stack (server-side) capabilities ──
export function detectsFullStackIntent(text) {
  // First check for explicit client-side / offline signals that OVERRIDE detection
  const clientSideSignals = [
    /\b(single[\s-]?file|single[\s-]?html|one[\s-]?html|offline|no[\s-]?backend|no[\s-]?server|no[\s-]?api|zero[\s-]?backend|without[\s-]?internet|client[\s-]?side[\s-]?only|localStorage[\s-]?(?:as|only|based)|vanilla[\s-]?javascript|no[\s-]?external[\s-]?(?:dependencies|apis|services))\b/i,
    /\b(run[\s-]?(?:entirely|fully)[\s-]?in[\s-]?(?:the[\s-]?)?browser)\b/i,
    /\b(no[\s-]?(?:build[\s-]?tools|frameworks|libraries))\b/i,
    /\brequire[\s-]?no[\s-]?(?:apis|backend|server)/i,
    /\bfully[\s-]?(?:offline|local|private)\b/i,
  ];

  if (clientSideSignals.some(p => p.test(text))) {
    return false; // Explicitly a client-side app
  }

  const fullStackSignals = [
    /\b(backend|back-end|server|server[\s-]?side|api\s+endpoint|rest\s+api|graphql)\b/i,
    /\b(postgres|mysql|mongodb|sqlite|supabase|firebase\s+(?:firestore|realtime))\b/i,
    /\b(user\s+auth|authentication|sign[\s-]?up|log[\s-]?in\s+system|oauth|jwt|session[\s-]?management)\b/i,
    /\b(send\s+email|smtp|email\s+notification|push\s+notification|sms|twilio|whatsapp\s+api)\b/i,
    /\b(payment|stripe|paypal|yoco|checkout\s+integration|billing\s+system)\b/i,
    /\b(real[\s-]?time\s+(?:chat|sync|update|collaboration|multiplayer))\b/i,
    /\b(file\s+upload|image\s+upload|cloud\s+storage|s3|blob\s+storage)\b/i,
    /\b(cron\s+job|scheduled\s+task|background\s+worker|queue|webhook)\b/i,
    /\b(deploy\s+to|hosting|docker|vercel\s+functions|serverless\s+function|lambda)\b/i,
    /\b(multi[\s-]?user|role[\s-]?based|admin\s+panel\s+with\s+(?:real|live)\s+data)\b/i,
    /\b(third[\s-]?party\s+api|external\s+api|fetch\s+from\s+(?:server|api))\b/i,
    /\b(websocket|socket\.io|sse|server[\s-]?sent\s+events)\b/i,
  ];

  const matches = fullStackSignals.filter(p => p.test(text));
  return matches.length >= 2; // Require at least 2 signals (reduces false positives)
}

// Build a user-friendly advisory message
function buildFullStackAdvisory(text) {
  const features = [];

  if (/\b(backend|back-end|server|server[\s-]?side|api\s+endpoint|rest\s+api|graphql)\b/i.test(text))
    features.push('server/API endpoints');
  if (/\b(database|postgres|mysql|mongodb|sqlite|supabase|firebase\s+(?:firestore|realtime))\b/i.test(text))
    features.push('database storage');
  if (/\b(user\s+auth|authentication|sign[\s-]?up|log[\s-]?in\s+system|oauth|jwt|session[\s-]?management)\b/i.test(text))
    features.push('user authentication');
  if (/\b(send\s+email|smtp|email\s+notification|push\s+notification|sms|twilio|whatsapp\s+api)\b/i.test(text))
    features.push('email/notifications');
  if (/\b(payment|stripe|paypal|yoco|checkout\s+integration|billing\s+system)\b/i.test(text))
    features.push('payment processing');
  if (/\b(real[\s-]?time\s+(?:chat|sync|update|collaboration|multiplayer))\b/i.test(text))
    features.push('real-time sync');
  if (/\b(file\s+upload|image\s+upload|cloud\s+storage|s3|blob\s+storage)\b/i.test(text))
    features.push('file uploads');
  if (/\b(websocket|socket\.io|sse|server[\s-]?sent\s+events)\b/i.test(text))
    features.push('WebSocket/real-time connections');
  if (/\b(multi[\s-]?user|role[\s-]?based)\b/i.test(text))
    features.push('multi-user/roles');

  return features.slice(0, 3);
}

// ── Orchestrator: decides simple vs complex ──
export async function callAI() {
  setStatus('thinking');
  showTyping();

  const userMsg = S.history[S.history.length - 1]?.content || '';

  try {
    // Full-Stack mode — generate complete Next.js project
    if (isFullStackMode() && isProPlus()) {
      await fullStackBuild(userMsg);
      setStatus('done');
      setTimeout(() => { setStatus(''); hideSteps(); }, 3000);
      return;
    }

    // ── GUARDRAIL: Detect full-stack intent in client-side mode ──
    // Only check for new builds (not modifications to existing code)
    // Skip if user is responding to a previous advisory (said "continue" or similar)
    const hasExistingCode = S.code && S.code.trim().length > 100;
    const lastAssistantMsg = [...S.history].reverse().find(m => m.role === 'assistant')?.content || '';
    const wasAdvisory = lastAssistantMsg.includes('Switch to Full-Stack mode');
    const userAccepted = /\b(continue|go\s+ahead|build\s+it|proceed|client[\s-]?side|just\s+build)\b/i.test(userMsg);

    if (!hasExistingCode && !isFullStackMode() && detectsFullStackIntent(userMsg) && !wasAdvisory && !userAccepted) {
      removeTyping();
      const features = buildFullStackAdvisory(userMsg);
      const featureList = features.length > 0
        ? features.map(f => `• ${f}`).join('\n')
        : '• server-side functionality';

      const advisory = `⚠️ **Heads up!** Your prompt looks like it needs full-stack features:\n\n${featureList}\n\nI'm currently in **client-side mode** — I build single HTML apps that run entirely in the browser (using localStorage for data). These features need a real server/backend to work properly.\n\n**Your options:**\n1. **Switch to Full-Stack mode** (Pro+) — I'll generate a complete Next.js project with backend, database, and API routes\n2. **Continue in client-side mode** — I'll build the best possible version using localStorage and browser APIs (no real server, auth, or external integrations)\n\nType "continue" to build client-side, or switch to Full-Stack mode in the toolbar ⚡`;

      addMsg('assistant', advisory, true);
      S.history.push({ role: 'assistant', content: advisory });

      // Save conversation state and wait for user's next message
      saveConversation();
      setStatus('');
      return;
    }

    // ── Check if user said "continue" after a full-stack advisory ──
    // (handled naturally — the next callAI will proceed since the advisory was already shown)

    // Standard mode — classify: does this need planning?
    const needsPlan = isComplexRequest(userMsg);

    if (needsPlan) {
      await complexBuild(userMsg);
    } else {
      await simpleBuild();
    }

    setStatus('done');
    setTimeout(() => { setStatus(''); hideSteps(); }, 3000);
  } catch (err) {
    removeTyping();
    hideSteps();
    console.error('[KasiCode] Build error:', err);
    addMsg('assistant', '⚠️ Could not connect to AI. Check your internet and try again.');
    setStatus('');
  }
}

export function isComplexRequest(text) {
  // If there's existing code, NEVER do a complex rebuild — always modify
  if (S.code && S.code.trim().length > 100) return false;

  // Heuristics for complex requests that benefit from planning (new builds only)
  const complexSignals = [
    /\b(full|complete|entire)\s+(app|application|website|system|tool)/i,
    /\bwith\b.*\band\b.*\band\b/i,                    // "with X and Y and Z"
    /\b(multiple|several|different)\s+(pages?|sections?|features?|screens?)/i,
    /\b(dashboard|admin|portal|platform|marketplace)/i,
    /\b(database|backend|api|auth|login|signup)/i,
    /\b(crud|create.*read.*update|manage.*data)/i,
  ];
  return complexSignals.some(p => p.test(text));
}

export function isModificationRequest(text) {
  // If there's existing code, EVERYTHING is a modification unless user explicitly wants a fresh start
  if (S.code && S.code.trim().length > 100) {
    // Only treat as "new build" if user explicitly says so
    const newBuildSignals = [
      /\b(start\s+over|from\s+scratch|new\s+app|build\s+a\s+new|create\s+a\s+new|fresh\s+start|scrap\s+this|start\s+fresh)\b/i,
      /\b(build\s+me\s+a|make\s+me\s+a|create\s+a)\s+\w+\s+(app|tool|calculator|tracker|system|page|website)/i,
    ];
    // If it matches a "new build" signal, it's NOT a modification
    return !newBuildSignals.some(p => p.test(text));
  }

  // No existing code — use heuristics to detect if it's a modification-style request
  // (this handles edge cases where code was just cleared)
  const modSignals = [
    /\b(change|update|modify|edit|tweak|adjust|fix|move|swap)\b/i,
    /\b(make\s+(it|the|this))\b/i,
    /\b(add|remove|delete|hide|show)\s+(a|the|an|some)?\s?\w/i,
    /\b(bigger|smaller|larger|wider|taller|shorter)\b/i,
    /\b(color|colour|font|size|spacing|padding|margin|border|background)\b/i,
    /\b(instead|replace|switch|rename)\b/i,
    /\b(more|less)\s+(modern|professional|clean|minimal|bold|colorful)/i,
    /\b(can you|could you|please)\s+(change|update|make|add|remove|fix)/i,
  ];
  return modSignals.some(p => p.test(text));
}

// ── Simple build: single Coder call + debug loop + review ──
export async function simpleBuild() {
  const lastMsg = S.history[S.history.length - 1]?.content || '';
  const isMod = S.code && isModificationRequest(lastMsg);
  const systemPrompt = buildSystem('coder');
  const messages = S.history.map(m => ({ role: m.role, content: m.content }));

  // Use Sonnet for NEW builds (quality matters for first impression)
  // Use Haiku for modifications (fast, structure already exists)
  const model = isMod ? MODELS.HAIKU : MODELS.SONNET;

  showSteps([isMod ? 'Applying changes' : 'Generating code', 'Testing & fixing', 'Reviewing quality', 'Done']);
  updateStep(0, 'active');
  await sleep(100); // Let UI render before stream starts

  const reply = await aiRequestStream(systemPrompt, messages, model);
  if (!reply) return;

  S.history.push({ role: 'assistant', content: reply });

  const code = extractCode(reply);
  if (!code) { updateStep(0, 'done'); updateStep(3, 'done'); return; }

  updateStep(0, 'done');
  updateStep(1, 'active');

  // Auto-apply code to editor and run
  applyCode(code);
  runCode();
  notifyCodeUpdate();

  // ── Debug loop: catch JS errors and visual issues ──
  let currentCode = code;
  const debugged = await debugLoop(currentCode, 0);
  if (debugged !== currentCode) {
    currentCode = debugged;
    applyCode(currentCode);
    runCode();
    notifyCodeUpdate();
  }

  updateStep(1, 'done');
  updateStep(2, 'active');

  // ── Reviewer: check for dead buttons, static mockups, broken features ──
  const reviewResult = await reviewCode(currentCode);
  if (reviewResult.issues.length > 0) {
    updateStatusPill('Fixing ' + reviewResult.issues.length + ' issue(s)...');

    const fixPrompt = buildSystem('coder');
    const fixReply = await aiRequestSilent(fixPrompt, [
      { role: 'user', content: `Fix these issues in the code. Return the COMPLETE fixed code:\n\n${reviewResult.issues.map((issue, i) => (i+1) + '. ' + issue).join('\n')}\n\nCode:\n\`\`\`\n${currentCode.slice(0, 5000)}\n\`\`\`\n\nReturn the COMPLETE fixed code in a \`\`\`html\`\`\` block.` }
    ], MODELS.HAIKU);

    if (fixReply) {
      const fixedCode = extractCode(fixReply);
      if (fixedCode) {
        currentCode = fixedCode;
        applyCode(currentCode);
        runCode();
        notifyCodeUpdate();
        addMsg('assistant', `🔧 Auto-fixed ${reviewResult.issues.length} issue${reviewResult.issues.length > 1 ? 's' : ''} found during review.`, false);
      }
    }
  }

  updateStep(2, 'done');

  S.builds++;
  updateStep(3, 'done');

  // Notify if app needs cloud sync for full functionality
  checkCloudSyncNeeded();

  // Cache this build if it's a new template build (not a modification)
  if (!isMod && code) {
    cacheTemplate(lastMsg, code);
  }

  // Auto-save to projects
  autoSaveProject();

  // Persist conversation
  saveConversation();
}

// ── Complex build: Planner → step-by-step Coder → Reviewer ──
export async function complexBuild(userMsg) {
  // Step 1: Plan
  updateStatusPill('Planning...');
  showSteps(['Planning steps']);
  updateStep(0, 'active');

  const planPrompt = buildSystem('planner');
  const planReply = await aiRequest(planPrompt, [
    { role: 'user', content: userMsg }
  ], MODELS.HAIKU);  // Planner output is short — Haiku is fine
  if (!planReply) return;

  removeTyping();

  // Parse numbered steps from planner output
  const steps = parsePlanSteps(planReply);
  if (steps.length === 0) {
    // Planner didn't return steps — fall back to simple build
    S.history.push({ role: 'assistant', content: planReply });
    addMsg('assistant', planReply, true);
    updateStep(0, 'done');
    return simpleBuild();
  }

  // Show plan in chat
  const planText = `📋 I'll build this in ${steps.length} steps:\n\n${steps.map((s, i) => `${i + 1}. ${s}`).join('\n')}\n\nStarting now...`;
  addMsg('assistant', planText, false);

  // Update step UI with actual plan
  updateStep(0, 'done');
  const stepLabels = steps.map((s, i) => `Step ${i + 1}: ${s.slice(0, 50)}${s.length > 50 ? '...' : ''}`);
  stepLabels.push('Review & polish');
  stepLabels.push('Done');
  showSteps(stepLabels);

  // Step 2: Execute each step
  let currentCode = S.code || '';

  for (let i = 0; i < steps.length; i++) {
    updateStep(i, 'active');
    updateStatusPill(`Building step ${i + 1}/${steps.length}...`);

    const stepPrompt = buildSystem('coder');
    const stepContext = currentCode
      ? `Current code so far:\n\`\`\`\n${currentCode.slice(0, 3000)}\n\`\`\`\n\nNow implement this next step: ${steps[i]}\n\nReturn the COMPLETE updated code (not just the new part).`
      : `Build this: ${steps[i]}\n\nReturn the complete code.`;

    const stepReply = await aiRequestSilent(stepPrompt, [
      { role: 'user', content: userMsg },
      ...(currentCode ? [{ role: 'assistant', content: 'Here is the code so far.' }] : []),
      { role: 'user', content: stepContext }
    ], MODELS.HAIKU);

    if (!stepReply) { updateStep(i, 'error'); continue; }

    const stepCode = extractCode(stepReply);
    if (stepCode) {
      currentCode = stepCode;
      applyCode(currentCode);

      // Debug this step
      const debugged = await debugLoop(currentCode, 0);
      if (debugged !== currentCode) {
        currentCode = debugged;
        applyCode(currentCode);
      }
    }

    updateStep(i, 'done');
  }

  // Step 3: Reviewer agent
  const reviewIdx = steps.length;
  updateStep(reviewIdx, 'active');
  updateStatusPill('Reviewing...');

  const reviewResult = await reviewCode(currentCode);
  if (reviewResult.issues.length > 0) {
    addMsg('assistant', `🔍 Review found ${reviewResult.issues.length} issue${reviewResult.issues.length > 1 ? 's' : ''} — fixing...`, false);

    const fixPrompt = buildSystem('coder');
    const fixReply = await aiRequestSilent(fixPrompt, [
      { role: 'user', content: `Fix these issues in the code:\n\n${reviewResult.issues.join('\n')}\n\nCode:\n\`\`\`\n${currentCode.slice(0, 3500)}\n\`\`\`\n\nReturn the COMPLETE fixed code.` }
    ], MODELS.HAIKU);

    if (fixReply) {
      const fixedCode = extractCode(fixReply);
      if (fixedCode) {
        currentCode = fixedCode;
        applyCode(currentCode);
      }
    }
  }

  updateStep(reviewIdx, 'done');

  // Final
  runCode();
  notifyCodeUpdate();
  S.builds++;

  S.history.push({ role: 'assistant', content: `Built your app in ${steps.length} steps. Code is ready!` });
  addMsg('assistant', `✅ Built in ${steps.length} steps! Your app is running in the preview.\n\nYou can tweak it by describing changes, or deploy it with 🚀`, true);

  const doneIdx = steps.length + 1;
  updateStep(doneIdx, 'done');

  // Auto-save to projects
  autoSaveProject();

  // Persist conversation
  saveConversation();
}

// ── Reviewer agent (with visual data) ──
async function reviewCode(code) {
  // Run one more time to get fresh visual report
  _visualReport = null;
  runCodeSilent(code);
  await sleep(2000);

  let visualContext = '';
  if (_visualReport) {
    if (_visualReport.issues?.length > 0) {
      visualContext = '\n\nVisual inspection found these issues:\n' + _visualReport.issues.join('\n');
    }
    if (_visualReport.summary) {
      const s = _visualReport.summary;
      visualContext += `\n\nPage summary: title="${s.title}", ${s.elements} elements, ${s.headings} headings, ${s.buttons} buttons, ${s.inputs} inputs, ${s.images} images, viewport ${s.viewportWidth}px, scroll width ${s.scrollWidth}px`;
    }
  }

  const reviewPrompt = buildSystem('reviewer');
  const reply = await aiRequest(reviewPrompt, [
    { role: 'user', content: `Review this code:${visualContext}\n\n\`\`\`\n${code.slice(0, 4000)}\n\`\`\`` }
  ], MODELS.HAIKU);

  if (!reply) return { issues: [], ok: true };

  // Parse issues from reviewer response
  const issues = [];
  const lines = reply.split('\n');
  for (const line of lines) {
    const m = line.match(/^\s*(?:\d+[\.\)]\s*|[-•]\s+|⚠️?\s*)(.+)/);
    if (m && m[1].trim().length > 10) {
      issues.push(m[1].trim());
    }
  }

  // If reviewer says "no issues" or similar, return clean
  if (/no\s+(major\s+)?issues|looks\s+good|code\s+is\s+(clean|fine|good)/i.test(reply)) {
    return { issues: [], ok: true };
  }

  return { issues: issues.slice(0, 5), ok: issues.length === 0 };
}

// ── Debug loop — catches JS errors AND visual issues ──
async function debugLoop(code, attempt) {
  if (attempt >= AGENT_MAX_RETRIES) return code;

  _iframeErrors = [];
  _visualReport = null;
  runCodeSilent(code);
  await sleep(2000); // extra time for visual inspector

  const jsErrors = _iframeErrors.length > 0;
  const visualIssues = _visualReport?.issues?.length > 0;

  if (!jsErrors && !visualIssues) return code; // Clean run!

  // Build error context for the debugger
  let errorContext = '';
  if (jsErrors) {
    errorContext += 'JavaScript errors:\n' + _iframeErrors.slice(0, 5).join('\n') + '\n\n';
  }
  if (visualIssues) {
    errorContext += 'Visual/layout issues:\n' + _visualReport.issues.join('\n') + '\n\n';
    if (_visualReport.summary) {
      const s = _visualReport.summary;
      errorContext += `Page info: ${s.elements} elements, ${s.headings} headings, ${s.buttons} buttons, ${s.inputs} inputs, viewport ${s.viewportWidth}px, scroll width ${s.scrollWidth}px\n`;
    }
  }

  const totalIssues = (_iframeErrors?.length || 0) + (_visualReport?.issues?.length || 0);
  updateStatusPill(`Fixing ${totalIssues} issue${totalIssues > 1 ? 's' : ''} (attempt ${attempt + 1}/${AGENT_MAX_RETRIES})...`);

  const debugPrompt = buildSystem('debugger');
  const debugMessages = [
    { role: 'user', content: `The following code has issues. Fix them and return the complete corrected code.\n\n${errorContext}\nCode:\n\`\`\`\n${code}\n\`\`\`` }
  ];

  const fixReply = await aiRequestSilent(debugPrompt, debugMessages, MODELS.HAIKU);
  if (!fixReply) return code;

  const fixedCode = extractCode(fixReply);
  if (!fixedCode) return code;

  const issueTypes = [jsErrors ? 'errors' : '', visualIssues ? 'layout issues' : ''].filter(Boolean).join(' & ');
  addMsg('assistant', `🔧 Fixed ${totalIssues} ${issueTypes} — retesting...`, false);
  return debugLoop(fixedCode, attempt + 1);
}
